import type { ReactNode } from 'react'
import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import {
  resolveAssetUrl,
  type ImageLike,
} from '@sportninja/common/utils/assetUrl'
import Text from 'src/components/ts-components/Text'

const COMPETITION_PLACEHOLDER =
  '/images/generic_placeholders/placeholder_competition.png'

interface RegistrationHeaderProps {
  scheduleId?: string | null
  scheduleName?: string | null
  scheduleImage?: ImageLike
  isTeamRegistration: boolean
  /** Overrides the Team/Player wording, e.g. a Team Rep's own budget line (SN-8480). */
  label?: string
  /** Rendered at the bottom right, under the modal's close button (e.g. ID chips). */
  actions?: ReactNode
  /**
   * Starts the actions below a modal's close button (top-right) instead of
   * centring them. Set it when the header is a modal title.
   */
  clearCloseButton?: boolean
}

const RegistrationHeader = ({
  scheduleId,
  scheduleName,
  scheduleImage,
  isTeamRegistration,
  label,
  actions,
  clearCloseButton = false,
}: RegistrationHeaderProps) => {
  const logoSrc = resolveAssetUrl(scheduleImage) ?? COMPETITION_PLACEHOLDER

  const title = (
    <Text variant='displaySmall' color={colors.NEUTRAL_0} weight='semiBold'>
      {scheduleName || ''}
    </Text>
  )

  return (
    <Container>
      <Logo
        src={logoSrc}
        alt='Competition logo'
        width={72}
        height={72}
        onError={(e) => {
          const img = e.target as HTMLImageElement
          img.onerror = null
          img.src = COMPETITION_PLACEHOLDER
        }}
      />
      <Info>
        {scheduleId ? (
          <TitleLink
            href={`${ROUTES.SCHEDULE_ROOT}/${scheduleId}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            {title}
          </TitleLink>
        ) : (
          title
        )}
        <Text
          variant='captionLarge'
          color={colors.NEUTRAL_50}
          weight='semiBold'
        >
          {label ??
            (isTeamRegistration ? 'Team Registration' : 'Player Registration')}
        </Text>
      </Info>
      {actions ? (
        <Actions clearCloseButton={clearCloseButton}>{actions}</Actions>
      ) : null}
    </Container>
  )
}

export default RegistrationHeader

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

// One chip per row, right-aligned. In a modal they start just under its close
// button: the top padding clears it (it ends 28px below this header's top edge).
const Actions = styled.div<{ clearCloseButton: boolean }>`
  margin-left: auto;
  align-self: ${({ clearCloseButton }) =>
    clearCloseButton ? 'flex-start' : 'center'};
  padding-top: ${({ clearCloseButton }) => (clearCloseButton ? 32 : 0)}px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`

const Logo = styled.img`
  border-radius: 8px;
  object-fit: contain;
  background: ${colors.NEUTRAL_0};
  flex-shrink: 0;
`

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TitleLink = styled.a`
  color: ${colors.NEUTRAL_0};
  text-decoration: none;
  &:hover {
    color: ${colors.NEUTRAL_0};
    text-decoration: underline;
  }
`
